import type { AuthResponse, SafeUser } from "@playarena/shared";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { AuthState } from "@/types/api";

import { authApi } from "../api/authApi";
import { userApi } from "../api/userApi";

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("token"),
  refreshToken: localStorage.getItem("refreshToken"),
  isAuthenticated: !!localStorage.getItem("token"),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<AuthResponse>) => {
      state.token = action.payload.tokens.accessToken;
      state.refreshToken = action.payload.tokens.refreshToken;
      state.isAuthenticated = true;
      if (action.payload.user) {
        state.user = action.payload.user;
      }
      localStorage.setItem("token", action.payload.tokens.accessToken);
      localStorage.setItem("refreshToken", action.payload.tokens.refreshToken);
    },
    updateAvatar: (state, action: PayloadAction<string | null>) => {
      if (state.user) {
        state.user.profile.avatar = action.payload || "";
      }
    },
    setTokens: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
      }>
    ) => {
      state.token = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      localStorage.setItem("token", action.payload.accessToken);
      localStorage.setItem("refreshToken", action.payload.refreshToken);
    },
    logout: state => {
      state.token = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    },
    setUser: (state, action: PayloadAction<SafeUser>) => {
      state.user = action.payload;
    },
  },
  extraReducers: builder => {
    // Handle successful login/register/googleAuth
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        if (action.payload.success && action.payload.data) {
          state.token = action.payload.data.tokens.accessToken;
          state.refreshToken = action.payload.data.tokens.refreshToken;
          state.user = action.payload.data.user;
          state.isAuthenticated = true;
        }
      })
      .addMatcher(authApi.endpoints.register.matchFulfilled, (state, action) => {
        if (action.payload.success && action.payload.data) {
          state.token = action.payload.data.tokens.accessToken;
          state.refreshToken = action.payload.data.tokens.refreshToken;
          state.user = action.payload.data.user;
          state.isAuthenticated = true;
        }
      })
      .addMatcher(authApi.endpoints.googleAuth.matchFulfilled, (state, action) => {
        if (action.payload.success && action.payload.data) {
          state.token = action.payload.data.tokens.accessToken;
          state.refreshToken = action.payload.data.tokens.refreshToken;
          state.user = action.payload.data.user;
          state.isAuthenticated = true;
        }
      })
      .addMatcher(authApi.endpoints.refreshToken.matchFulfilled, (state, action) => {
        if (action.payload.success && action.payload.data) {
          state.token = action.payload.data.accessToken;
          state.refreshToken = action.payload.data.refreshToken;
          state.isAuthenticated = true;
        }
      })
      .addMatcher(userApi.endpoints.getCurrentUser.matchFulfilled, (state, action) => {
        if (action.payload.success && action.payload.data) {
          state.user = action.payload.data;
        }
      })
      // Handle logout completion
      .addMatcher(authApi.endpoints.logout.matchFulfilled, state => {
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
      });
  },
});

export const { setCredentials, updateAvatar, setTokens, logout, setUser } = authSlice.actions;
export default authSlice.reducer;
